import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/sima";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const sima: CaseStudy = {
  slug: "sima",
  title: "Sima",
  cardTitle: "Корпоративный сайт для поставщика оборудования Sima",
  subtitle: "Поставщик оборудования для переработки мяса и птицы",
  industry: { label: "Логистика и промышленность", slug: "logistics" },
  eyebrow: "Корпоративный сайт · Промышленное оборудование",
  h1: "Разработка сайта для поставщика оборудования Sima",
  lead:
    "Собрали корпоративный B2B-сайт для Sima — поставщика автоматизированных решений для переработки мяса и птицы: каталог оборудования, готовые производственные линии, проектирование и сервис, форма коммерческого запроса.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить B2B-сайт" },

  seo: {
    primaryKeyword: "разработка сайта для поставщика промышленного оборудования",
    secondaryKeywords: [
      "сайт поставщика оборудования",
      "корпоративный сайт промышленной компании",
      "сайт каталога оборудования",
      "разработка B2B сайта оборудования",
    ],
    semanticEntities: [
      "готовые производственные линии",
      "каталог оборудования",
      "бренды оборудования",
      "проектирование",
      "обслуживание",
      "коммерческий запрос",
    ],
    excludedKeywords: [
      "оборудование для колбас купить",
      "мясоперерабатывающее оборудование цена",
      "линия для сосисок",
      "куттер купить",
    ],
    title: "Корпоративный сайт Sima — кейс HAY.agency",
    description:
      "Кейс HAY.agency: B2B-сайт Sima с каталогом мясоперерабатывающего оборудования, готовыми производственными линиями, проектированием и сервисом.",
    h1: "Разработка сайта для поставщика оборудования Sima",
    slug: "sima",
    canonicalUrl: `${SITE_URL}/cases/sima`,
    ogTitle: "Корпоративный сайт Sima для поставщика оборудования",
    ogDescription:
      "Каталог оборудования, готовые производственные линии, проектирование, обслуживание и форма коммерческого запроса.",
    ogImage: `${SITE_URL}${dir}/sima-og.webp`,
    robotsIndex: true,
    datePublished: "2024-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка корпоративных сайтов",
      to: routes.corporate,
      note: "Каталог, услуги и заявки для B2B",
    },
    relatedIndustry: {
      label: "Сайты для производственных и логистических компаний",
      to: routes.sites,
      note: "Каталоги оборудования и B2B-сценарии",
    },
    relatedCases: [
      {
        label: "Сайт-каталог для производителя заборов Artline",
        to: "/cases/artline",
        note: "B2B-каталог продукции с фильтрами",
      },
      {
        label: "Комплексная логистика System Logistic",
        to: "/cases/system-logistic",
        note: "Корпоративный сайт логистической компании",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Давайте обсудим, как мы можем масштабировать ваш бизнес",
    text: "Оставьте заявку — свяжемся в течение двух часов, чтобы задать вопросы и обсудить задачу.",
    button: "Обсудить B2B-сайт",
  },

  client: {
    name: "Sima",
    url: "https://sima.today/",
    city: "Ташкент",
    industry: "Промышленное оборудование",
  },
  year: "2026",
  services: [
    "Дизайн корпоративного сайта",
    "Каталог оборудования",
    "Разделы готовых производственных линий",
    "Раздел проектирования и обслуживания",
    "Форма коммерческого запроса",
  ],
  platform: "—",
  tags: [
    { label: "Корпоративный сайт", to: routes.corporate },
    { label: "Разработка", to: routes.sites },
  ],
  facts: [
    { label: "Клиент", value: "Sima" },
    { label: "Отрасль", value: "Промышленное оборудование" },
    { label: "Тип проекта", value: "B2B-сайт с каталогом оборудования" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог оборудования с категориями, брендами и характеристиками",
      "Готовые производственные линии, разбитые по типу конечной продукции и этапам",
      "Раздел проектирования и обслуживания как отдельный B2B-аргумент",
      "Форма получения коммерческого предложения",
      "Направления для Узбекистана, Таджикистана и англоязычных клиентов",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Sima поставляет автоматизированные решения для переработки мяса и птицы — от отдельного оборудования до готовых производственных линий. Покупатель в этой нише обычно отталкивается от результата производства, а не от названия конкретного станка, поэтому сайту нужна была логика выбора «от задачи к линии».",
        "Вторая часть задачи — показать сервис вокруг поставки: проектирование и обслуживание, которые для B2B-клиента часто важнее самой продажи оборудования.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: холодный индустриальный тон вместо типового B2B-шаблона",
    paragraphs: [
      "Первый экран построен на холодном grey/metal фоне с крупными фотографиями реального оборудования — это сразу считывается как промышленный B2B-продукт, а не универсальный корпоративный шаблон.",
      "Композиции собраны на нейтральной презентационной подложке: без открытых окон выбора города, cookie-баннеров и чатов, чтобы каждый мокап показывал конкретный сценарий сайта.",
    ],
    bullets: [
      "Холодная grey/metal палитра и фотографии оборудования",
      "Понятная навигация от задачи производства к конкретной линии",
      "Отдельный акцент на проектировании и обслуживании",
    ],
  },

  identity: {
    fonts: [
      { name: "Oswald", role: "Заголовки", sample: "Готовые линии" },
      { name: "PT Sans Narrow", role: "Текст и характеристики", sample: "Каталог оборудования" },
    ],
    colors: [
      { hex: "#EDEEF0", name: "Основной фон" },
      { hex: "#2B2E33", name: "Индустриальный акцент" },
      { hex: "#C6401F", name: "Акцент бренда" },
    ],
  },

  cover: img("sima-case-cover", "Главная страница корпоративного сайта Sima с каталогом оборудования"),

  gallery: [
    img("sima-lines", "Раздел готовых производственных линий на сайте Sima", 6),
    img("sima-catalog", "Каталог оборудования Sima с карточкой товара", 3),
    img("sima-project-form", "Раздел проектирования и форма коммерческого запроса Sima", 3),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику «от задачи к линии»: выбор типа продукции, подходящие линии, оборудование и форма запроса без лишних экранов.",
      "Смысловые карточки категорий и линий на мобильном идут горизонтальным скроллом, а не сжатой копией десктопной сетки.",
    ],
    images: [
      mobile("sima-mobile-home", "Мобильная главная сайта Sima"),
      mobile("sima-mobile-catalog", "Мобильный каталог оборудования Sima"),
    ],
  },

  columnBlocks: [
    {
      id: "flow",
      title: "От задачи к линии",
      paragraphs: [
        "Посетитель сначала выбирает результат производства, а затем переходит к конкретному оборудованию и линии — это ближе к тому, как B2B-клиент формулирует запрос при поиске поставщика.",
      ],
      columns: [
        { title: "Результат производства", items: ["Тип конечной продукции", "Этап переработки"] },
        { title: "Оборудование", items: ["Категория", "Бренд", "Характеристики"] },
        { title: "Решение", items: ["Готовая линия", "Проектирование", "Обслуживание"] },
      ],
    },
    {
      id: "catalog",
      title: "Каталог оборудования",
      paragraphs: [
        "Категории и карточка оборудования с характеристиками и брендом дают клиенту достаточно данных, чтобы сформировать запрос без звонка на первом шаге.",
      ],
      bullets: [
        "Категории оборудования по этапам переработки",
        "Карточка с брендом и техническими характеристиками",
        "Переход к запросу коммерческого предложения",
      ],
    },
    {
      id: "service",
      title: "Проектирование и обслуживание",
      paragraphs: [
        "Сервис вокруг поставки — отдельный раздел и самостоятельный B2B-аргумент, а не приписка к продаже оборудования.",
      ],
      bullets: ["Проектирование производственной линии", "Сервисное обслуживание", "Сопровождение поставки"],
    },
    {
      id: "request",
      title: "Получение предложения",
      paragraphs: [
        "Форма коммерческого запроса рассчитана на короткое обращение — без длинной анкеты, характерной для розничных сайтов.",
      ],
      bullets: ["Короткая форма запроса", "Направления для Узбекистана и Таджикистана", "Англоязычный раздел"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [
      { label: "Разработка корпоративного сайта", to: routes.corporate },
      { label: "Каталог оборудования", to: routes.sites },
    ],
  },
};
